import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getQuote, respondToQuote, sendQuote, type QuoteStatus } from "@/lib/quotes.functions";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { createQuoteCheckout } from "@/lib/stripe-connect.functions";
import { useState } from "react";

export const Route = createFileRoute("/_authenticated/predracun/$id")({
  head: () => ({ meta: [{ title: "Predračun — kupish.si" }] }),
  component: Page,
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-3xl px-6 py-24 text-destructive">
      {(error as Error).message}
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-24">Predračun ne obstaja.</div>
  ),
});

const STATUS_LABEL: Record<QuoteStatus, string> = {
  draft: "Osnutek",
  sent: "Poslan",
  accepted: "Sprejet",
  rejected: "Zavrnjen",
  expired: "Potekel",
};

function Page() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const fetchQuote = useServerFn(getQuote);
  const respondFn = useServerFn(respondToQuote);
  const sendFn = useServerFn(sendQuote);
  const payFn = useServerFn(createQuoteCheckout);
  const [paying, setPaying] = useState(false);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["quote", id],
    queryFn: () => fetchQuote({ data: { id } }),
  });

  const respondMut = useMutation({
    mutationFn: (action: "accept" | "reject") => respondFn({ data: { id, action } }),
    onSuccess: () => {
      toast.success("Odgovor poslan");
      qc.invalidateQueries({ queryKey: ["quote", id] });
      qc.invalidateQueries({ queryKey: ["received-quotes", user?.id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const sendMut = useMutation({
    mutationFn: () => sendFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Predračun poslan");
      qc.invalidateQueries({ queryKey: ["quote", id] });
      qc.invalidateQueries({ queryKey: ["sent-quotes", user?.id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading)
    return <div className="mx-auto max-w-3xl px-6 py-24 text-muted-foreground">Nalagam…</div>;
  if (error)
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-destructive">
        {(error as Error).message}
      </div>
    );
  if (!data)
    return (
      <div className="mx-auto max-w-3xl px-6 py-24">Predračun ne obstaja.</div>
    );

  return (
    <section className="mx-auto max-w-3xl px-6 py-16">
      <Link to="/dashboard" className="text-xs eyebrow hover:text-accent">
        ← Nadzorna plošča
      </Link>

      <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Predračun</div>
          <h1 className="font-serif text-4xl mt-2">{data.title}</h1>
          <div className="mt-2 text-sm text-muted-foreground">
            Od: {data.vendor?.store_name ?? "Prodajalec"} · {new Date(data.created_at).toLocaleDateString("sl-SI")}
          </div>
        </div>
        <span className="text-xs uppercase tracking-wider px-3 py-1 border border-border">
          {STATUS_LABEL[data.status]}
        </span>
      </div>

      <div className="mt-8 border border-border">
        <div className="p-5 border-b border-border bg-muted/30 text-sm">
          <div className="flex justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs text-muted-foreground">Kupec</div>
              <div>{data.buyer_name ?? data.buyer_email}</div>
              <div className="text-xs text-muted-foreground">{data.buyer_email}</div>
            </div>
            {data.valid_until && (
              <div>
                <div className="text-xs text-muted-foreground">Velja do</div>
                <div>{new Date(data.valid_until).toLocaleDateString("sl-SI")}</div>
              </div>
            )}
          </div>
        </div>

        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/20">
            <tr>
              <th className="text-left p-3 font-medium">Postavka</th>
              <th className="text-right p-3 font-medium w-16">Kol.</th>
              <th className="text-right p-3 font-medium w-24">Cena</th>
              <th className="text-right p-3 font-medium w-24">Skupaj</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(data.items ?? []).map((it) => (
              <tr key={it.id}>
                <td className="p-3">
                  <div>{it.title}</div>
                  {it.description && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {it.description}
                    </div>
                  )}
                </td>
                <td className="p-3 text-right">{Number(it.quantity)}</td>
                <td className="p-3 text-right">€{Number(it.unit_price).toFixed(2)}</td>
                <td className="p-3 text-right">€{Number(it.line_total).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t border-border">
            <tr>
              <td colSpan={3} className="p-3 text-right text-muted-foreground">
                Vmesni seštevek
              </td>
              <td className="p-3 text-right">€{Number(data.subtotal).toFixed(2)}</td>
            </tr>
            {Number(data.shipping) > 0 && (
              <tr>
                <td colSpan={3} className="p-3 text-right text-muted-foreground">
                  Dostava
                </td>
                <td className="p-3 text-right">€{Number(data.shipping).toFixed(2)}</td>
              </tr>
            )}
            <tr>
              <td colSpan={3} className="p-3 text-right font-medium">
                Skupaj
              </td>
              <td className="p-3 text-right font-serif text-xl text-accent">
                €{Number(data.total).toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>

        {data.notes && (
          <div className="p-5 border-t border-border text-sm">
            <div className="eyebrow mb-2">Opombe</div>
            <p className="whitespace-pre-wrap text-muted-foreground">{data.notes}</p>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        {data.status === "draft" && (
          <>
            <Link
              to="/predracun/uredi/$id"
              params={{ id }}
              className="border border-border px-5 py-2.5 text-sm hover:bg-muted"
            >
              Uredi
            </Link>
            <button
              disabled={sendMut.isPending}
              onClick={() => sendMut.mutate()}
              className="bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:opacity-90 disabled:opacity-50"
            >
              Pošlji kupcu →
            </button>
          </>
        )}
        {(data.status === "sent" || data.status === "accepted") && !data.paid_at && (
          data.vendor?.stripe_charges_enabled ? (
            <button
              disabled={paying}
              onClick={async () => {
                setPaying(true);
                try {
                  const res = await payFn({ data: { quote_id: id, origin: window.location.origin } });
                  window.location.href = res.url;
                } catch (e) {
                  toast.error((e as Error).message);
                  setPaying(false);
                }
              }}
              className="bg-accent text-accent-foreground px-5 py-2.5 text-sm hover:opacity-90 disabled:opacity-50"
            >
              {paying ? "Odpiram plačilo…" : "Plačaj s kartico →"}
            </button>
          ) : (
            <span className="text-xs text-muted-foreground self-center">
              Ta prodajalec sprejema plačila po UPN nalogu.
            </span>
          )
        )}
        {data.status === "sent" && (
          <>
            <button
              disabled={respondMut.isPending}
              onClick={() => respondMut.mutate("accept")}
              className="bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:opacity-90 disabled:opacity-50"
            >
              Sprejmi predračun
            </button>
            <button
              disabled={respondMut.isPending}
              onClick={() => respondMut.mutate("reject")}
              className="border border-destructive/40 text-destructive px-5 py-2.5 text-sm hover:bg-destructive/5 disabled:opacity-50"
            >
              Zavrni
            </button>
          </>
        )}
      </div>
    </section>
  );
}
